import React, { useCallback, useEffect, useRef } from "react";

import * as Sentry from "@sentry/react";
import { extractSheet } from "@repo/editor/extractor-factory";
import { useParams } from "react-router";
import { parseAsString, useQueryState } from "nuqs";
import { toast } from "sonner";

import { GetAssignmentProblemsResponseDto } from "../../dtos/responses/get-assignment-problems.response.dto";
import {
  beginSolveScope,
  captureSolveError,
  solveBreadcrumb,
} from "../../lib/error-tracking";
import { useBeforeUnloadGuard } from "../../hooks/use-before-unload-guard";
import { ApiError } from "../../query";
import {
  useGetAssignment,
  useGetSubmitAssignment,
  useSubmitAssignment,
  useTempSubmitAssignment,
} from "../../query/assignment";
import useAnswerStore, { AnswerMapType } from "../../stores/answer-store";
import AnswerSheet from "../answer-sheet";
import FactorTablesModal from "../factor-tables/factor-tables-modal";
import SolveBottomBar from "../solve/solve-bottom-bar";
import SolveProblemNav from "../solve/solve-problem-nav";
import SolveTopBar from "../solve/solve-top-bar";
import { showConfirm } from "../ui/confirm-dialog";

const SolveAssignmentForm = () => {
  const { assignmentId, courseId } = useParams<{
    assignmentId: string;
    courseId: string;
  }>();
  const { data: assignment } = useGetAssignment(assignmentId!);

  const questionRef = useRef<HTMLDivElement>(null);
  // 앱이 의도적으로 reload(임시저장/제출 성공)할 때는 이탈 경고를 억제하기 위한 플래그. BUG-003
  const internalReloadRef = useRef(false);
  const { mutate: submitAssignment, isPending: isSubmitting } =
    useSubmitAssignment();
  const { mutate: tempSubmitAssignment, isPending: isTempSubmitting } =
    useTempSubmitAssignment();
  const { data: assignmentSubmit } = useGetSubmitAssignment(assignmentId!);
  const isAnySubmitting = isSubmitting || isTempSubmitting;

  // solve 페이지에 머무는 동안 발생하는 모든 Sentry 이벤트에 과제 컨텍스트를 부착.
  useEffect(() => {
    const cleanup = beginSolveScope({ assignmentId, courseId });
    solveBreadcrumb("solve:open", { assignmentId, courseId });
    return cleanup;
  }, [assignmentId, courseId]);

  const [problemId, setProblemId] = useQueryState(
    "problemId",
    parseAsString.withDefault(""),
  );

  const { answerMap, resetAnswerMap, setSelectedId } = useAnswerStore();

  const problems = assignment?.data?.problems;
  const submitProblems = assignmentSubmit?.data?.problems;

  useEffect(() => {
    if (!problems?.length) return;
    if (!assignmentSubmit) return;

    const sessionKey = `initialized-${assignmentId}`;
    const isAlreadyInitialized = sessionStorage.getItem(sessionKey);

    // initialized 플래그는 과제별이지만 answerMap(sessionStorage)은 과제 간 공유돼
    // 마지막으로 reset된 과제의 데이터만 갖는다. 같은 탭에서 A→B→A로 돌아오면
    // 플래그는 true여도 answerMap엔 B의 문항만 남아 현재 과제(A)와 불일치한다.
    // 현재 과제의 문항이 answerMap에 실제로 있을 때만 초기화를 건너뛴다.
    const firstQuestionId = problems.flatMap((p) => p.questions)[0]?.id;
    const answerMapMatches = firstQuestionId
      ? answerMap[firstQuestionId] !== undefined
      : Object.keys(answerMap).length > 0;

    if (isAlreadyInitialized && answerMapMatches) {
      if (!problemId) {
        setProblemId(problems[0].id);
      }
      return;
    }

    try {
      const questionMap: Map<string, string> = new Map();
      const questionSelectMap: Map<string, string> = new Map();

      problems.forEach((problem) => {
        const { questions } = problem;
        questions.forEach((question) => {
          questionMap.set(question.id, question.answerHTML);
        });
      });

      submitProblems?.forEach((problem) => {
        const { questions } = problem;
        questions.forEach((question) => {
          if (question.questionType === "SELECT") {
            questionSelectMap.set(
              question.id,
              question?.answer?.selectedId ?? "",
            );
          } else {
            questionMap.set(question.id, question.submitAnswerHTML);
          }
        });
      });

      const initAnswerMap: AnswerMapType = {};

      problems.forEach((problem) => {
        const { questions, assignmentProblemId } = problem;
        questions.forEach((question) => {
          if (question.questionType === "SELECT") {
            initAnswerMap[question.id] = {
              assignmentProblemId: assignmentProblemId,
              html: "",
              inputs: [],
              selectedId: questionSelectMap.get(question.id) ?? "",
            };
          } else {
            const container = document.createElement("div");
            const answerHTML = questionMap.get(question.id) ?? "";
            container.innerHTML = answerHTML;
            const fields = container.querySelectorAll("input, select");
            const values = Array.from(fields).map((el) => {
              if (el instanceof HTMLSelectElement) {
                return (
                  el.getAttribute("value") ||
                  el.querySelector("option[selected]")?.getAttribute("value") ||
                  ""
                );
              } else {
                return el.getAttribute("value") || "";
              }
            });

            initAnswerMap[question.id] = {
              assignmentProblemId: assignmentProblemId,
              html: answerHTML,
              inputs: values,
            };
          }
        });
      });

      resetAnswerMap(initAnswerMap);

      sessionStorage.setItem(sessionKey, "true");

      if (!problemId) {
        setProblemId(problems[0].id);
      }
      solveBreadcrumb("solve:init-answer-map", {
        assignmentId,
        problemCount: problems.length,
      });
    } catch (err) {
      // 답안 초기화(HTML 파싱)는 학생 답안 복원 경로라 실패 시 반드시 알아야 한다.
      captureSolveError(err, "init-answer-map", { assignmentId, problemId });
      toast.error("답안을 불러오는 중 문제가 발생했습니다. 새로고침 해주세요.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problems, submitProblems, assignmentId]);

  const problem = assignment?.data?.problems.find((problem) => {
    return problem.id === problemId;
  });
  const problemIndex = assignment?.data?.problems.findIndex(
    (problem) => problem.id === problemId,
  );
  const totalCount = assignment?.data?.problems.length ?? 0;
  const currentIndex =
    problemIndex != null && problemIndex >= 0 ? problemIndex : 0;

  const remainingSubmissions =
    (
      assignmentSubmit?.data &&
      assignmentSubmit?.data?.problems.find(
        (problem) => problem.snapshotId === problemId,
      )
    )?.remainingSubmissions ??
    assignment?.data?.maxSubmissionCount ??
    0;

  // 현재까지 채점된 총점 / 과제 만점(제출 이력 없으면 점수 미표시). UX-04
  // 만점은 과제 문항 배점(problems[].score) 합으로 계산(제출 응답 maxScore는 미채워질 수 있음).
  const submitScore = assignmentSubmit?.data ? assignmentSubmit.data.score : null;
  const totalScore =
    assignment?.data?.problems?.reduce((sum, p) => sum + (p.score ?? 0), 0) ?? 0;

  // 답안 DOM 렌더 준비 신호. answerMap 객체(타이핑마다 레퍼런스 변경)를 deps에
  // 넣으면 채점 색칠 effect가 과실행돼 스택 오버플로우가 나므로, false→true로
  // 한 번만 바뀌는 안정적 primitive로 "DOM 준비됨"만 전달한다.
  const answersReady = Object.keys(answerMap).length > 0;

  // 풀이 중 브라우저 이탈(새로고침/뒤로가기/탭닫기) 시 미저장 답안 유실 경고.
  // 임시저장은 현재 문제만 서버 저장하므로, 저장 안 한 다른 문제 답안이 이탈로 사라지는 것을 막는다. BUG-003
  useBeforeUnloadGuard(
    useCallback(
      () => answersReady && !internalReloadRef.current,
      [answersReady],
    ),
  );

  useEffect(() => {
    if (!questionRef) return;
    if (!questionRef.current) return;
    if (!problem) return;
    if (!assignmentSubmit) return;
    if (!assignmentSubmit.data) return;

    try {
      const submitProblem = assignmentSubmit?.data?.problems.find(
        (submitProblem) => {
          return submitProblem.snapshotId === problemId;
        },
      );

      problem.questions.forEach((question) => {
        const submitQuestion = submitProblem?.questions.find(
          (submitQuestion) => {
            return submitQuestion.id === question.id;
          },
        );

        const questionContainer = questionRef.current?.querySelector(
          `[data-question-id="${question.id}"]`,
        ) as HTMLDivElement | null;

        if (!questionContainer) return;

        for (const entry of submitQuestion?.submitGradeEntries || []) {
          if (question.questionType === "SELECT") {
            const questionWrapper = questionContainer.querySelector(
              `[id="${question.id}"]`,
            ) as HTMLDivElement | null;
            if (questionWrapper) {
              questionWrapper.style.setProperty(
                "outline",
                `2px solid ${entry.isCorrect ? "blue" : "red"}`,
              );
              questionWrapper.style.setProperty("outline-offset", `4px`);
            }
          } else {
            const tr = questionContainer.querySelector(
              `[id="${entry.trId}"]`,
            ) as HTMLTableRowElement | null;

            if (tr) {
              tr.style.setProperty(
                "outline",
                `2px solid ${entry.isCorrect ? "blue" : "red"}`,
              );
              tr.style.setProperty("outline-offset", `-4px`);
            }
          }
        }
      });
    } catch (err) {
      // 채점 색칠은 DOM을 직접 조작해 타이밍/구조 변화에 취약하다. 실패해도 화면은
      // 죽지 않게 두되, 채점이 안 보이는 원인을 추적할 수 있게 보고한다.
      captureSolveError(err, "grading-colors", { assignmentId, problemId });
    }
  }, [problem, assignmentSubmit, problemId, answersReady]);

  // answerMap이 현재 과제의 문항을 담고 있을 때만 렌더(상호작용)를 허용한다.
  // 다른 과제에서 넘어온 직후엔 stale answerMap이 남아있어, 이 가드가 없으면
  // 현재 과제 문항을 stale map 위에 그려 라디오 클릭이 무시되거나 제출이 실패한다.
  const firstQuestionId = problems?.flatMap((p) => p.questions)?.[0]?.id;
  const isAnswerMapReady = firstQuestionId
    ? answerMap[firstQuestionId] !== undefined
    : Object.keys(answerMap).length > 0;
  if (!isAnswerMapReady) return <></>;

  const getSubmit = (
    problem: GetAssignmentProblemsResponseDto["problems"][0],
  ) => {
    const questions = problem?.questions.map((question) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let answer: any;
      if (question.questionType === "SELECT") {
        answer = {
          questionType: question.questionType,
          selectedId: answerMap[question.id].selectedId,
        };
      } else {
        answer = extractSheet({
          version: "v1",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          questionType: question.questionType as any,
          div: answerMap[question.id].html,
        });
      }

      return {
        questionId: question.id,
        answer: answer,
        answerHTML: answerMap[question.id].html,
      };
    });
    return {
      assignmentProblemId: problem.assignmentProblemId,
      questions,
    };
  };

  const handleSubmit = async () => {
    if (isAnySubmitting) return;
    if (!problem) return;
    let submit: ReturnType<typeof getSubmit>;
    try {
      submit = getSubmit(problem);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "assignment-submit" },
        extra: { assignmentId, problemId },
      });
      toast.error(err instanceof Error ? err.message : "답안을 확인해주세요.");
      return;
    }
    const result = await showConfirm({
      title: "답안 제출",
      description:
        "정말 제출하시겠습니까? \n답안을 제출하면 채점이 되고, 시도횟수가 차감됩니다.",
      variant: "default",
    });
    if (!result) return;
    solveBreadcrumb("submit:start", {
      assignmentId,
      problemId,
      remainingSubmissions,
      questionCount: submit.questions?.length,
    });
    submitAssignment(
      {
        assignmentId: assignmentId!,
        ...submit,
      },
      {
        onSuccess: () => {
          sessionStorage.removeItem(`initialized-${assignmentId}`);
          sessionStorage.removeItem("answer-store");

          toast.success("제출완료했습니다.");
          internalReloadRef.current = true; // 의도적 reload — 이탈 경고 억제
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
        onError: (err) => {
          // 마감/시도횟수 초과 같은 4xx는 "버그"가 아니라 기대된 비즈니스 거절이다.
          // 서버 메시지(예: "제출 마감 시간이 지났습니다.")는 전역 MutationCache.onError가
          // 토스트로 노출하므로, 여기서는 진단용 breadcrumb만 남기고 Sentry 에러로는 보고하지 않는다.
          if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
            solveBreadcrumb("submit:rejected", {
              assignmentId,
              problemId,
              remainingSubmissions,
              status: err.status,
              code: err.code,
              message: err.message,
            });
            return;
          }
          // 그 외(5xx/네트워크 등)는 학생 입장에서 가장 치명적 — 컨텍스트와 함께 Sentry 보고.
          captureSolveError(err, "submit:onError", {
            assignmentId,
            problemId,
            remainingSubmissions,
          });
        },
      },
    );
  };

  const handleTempSubmit = async () => {
    if (isAnySubmitting) return;
    if (!problem) return;
    let submit: ReturnType<typeof getSubmit>;
    try {
      submit = getSubmit(problem);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "assignment-temp-submit" },
        extra: { assignmentId, problemId },
      });
      toast.error(err instanceof Error ? err.message : "답안을 확인해주세요.");
      return;
    }
    const result = await showConfirm({
      title: "임시저장",
      description:
        "정말 임시저장하시겠습니까? \n현재 문제의 답안만 저장됩니다. 다른 문제는 각 문제에서 임시저장해 주세요.",
      variant: "default",
    });
    if (!result) return;

    solveBreadcrumb("temp-submit:start", {
      assignmentId,
      problemId,
      questionCount: submit.questions?.length,
    });
    tempSubmitAssignment(
      {
        assignmentId: assignmentId!,
        ...submit,
      },
      {
        onSuccess: () => {
          sessionStorage.removeItem(`initialized-${assignmentId}`);
          sessionStorage.removeItem("answer-store");

          toast.success("임시저장 완료했습니다.");
          internalReloadRef.current = true; // 의도적 reload — 이탈 경고 억제
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
        onError: (err) => {
          // submit과 동일하게, 4xx 거절은 전역 핸들러가 서버 메시지를 토스트한다.
          if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
            solveBreadcrumb("temp-submit:rejected", {
              assignmentId,
              problemId,
              status: err.status,
              code: err.code,
              message: err.message,
            });
            return;
          }
          captureSolveError(err, "temp-submit:onError", {
            assignmentId,
            problemId,
          });
        },
      },
    );
  };

  const handlePrev = () => {
    if (!problems || currentIndex <= 0) return;
    solveBreadcrumb("nav:prev", { from: currentIndex, to: currentIndex - 1 });
    setProblemId(problems[currentIndex - 1].id);
  };

  const handleNext = () => {
    if (!problems || currentIndex >= totalCount - 1) return;
    solveBreadcrumb("nav:next", { from: currentIndex, to: currentIndex + 1 });
    setProblemId(problems[currentIndex + 1].id);
  };

  return (
    <>
      <SolveTopBar
        title={assignment?.data?.title ?? "과제"}
        courseId={courseId}
        remainingSubmissions={remainingSubmissions}
        maxSubmissions={assignment?.data?.maxSubmissionCount ?? 0}
        submitScore={submitScore}
        totalScore={totalScore}
      />

      <SolveProblemNav
        problems={assignment?.data?.problems ?? []}
        submitProblems={submitProblems}
        problemId={problemId}
        onSelect={setProblemId}
      />

      <main className="w-full">
        <div className="w-full max-w-[1200px] mx-auto px-2 md:px-4 lg:px-6 py-4 md:py-6 flex flex-col gap-6">
          <div className="text-lg font-bold text-gray-900">
            문제 {currentIndex + 1}
          </div>

          {problem && (
            <section className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-gray-700">공통 영역</p>
              <div
                className="border border-gray-200 rounded-lg shadow-sm bg-white w-full p-3 md:p-4 overflow-x-auto"
                dangerouslySetInnerHTML={{
                  __html: problem.problemHTML,
                }}
              />
            </section>
          )}

          <div className="flex flex-col gap-6" ref={questionRef}>
            {problem?.questions.map((question, questionIndex) => (
              <section
                key={question.id}
                data-question-id={question.id}
                className="flex flex-col gap-3"
              >
                {questionIndex > 0 && <hr className="border-gray-200" />}
                <div className="text-base md:text-lg font-bold border-l-4 border-blue-500 pl-2">
                  {questionIndex + 1}번 질문
                </div>

                {question.questionHTML && (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-semibold text-gray-700">
                      질문 영역
                    </p>
                    <div
                      className="border border-gray-200 rounded-lg shadow-sm bg-white w-full p-3 md:p-4 overflow-x-auto"
                      dangerouslySetInnerHTML={{
                        __html: question.questionHTML,
                      }}
                    />
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-gray-700">
                    답안 영역
                  </p>

                  {question.questionType != "SELECT" && (
                    <div className="border border-gray-200 rounded-lg shadow-sm bg-white w-full p-3 md:p-4 overflow-x-auto">
                      <AnswerSheet id={question.id} />
                    </div>
                  )}

                  {question.questionType === "SELECT" && (
                    <div
                      className="w-full flex flex-col gap-3"
                      id={question.id}
                    >
                      {question.choices.map((choice, index) => {
                        return (
                          <React.Fragment key={choice.id}>
                            <div className="flex gap-3 items-center">
                              <input
                                type="radio"
                                id={choice.id}
                                name={`question-${question.id}`}
                                checked={
                                  answerMap[question.id]?.selectedId ===
                                  choice.id
                                }
                                onChange={() => {
                                  setSelectedId(question.id, choice.id);
                                }}
                              />
                              <label
                                htmlFor={choice.id}
                                className="text-sm font-medium"
                              >
                                {index + 1}번 보기
                              </label>
                            </div>
                            <div className="border border-gray-200 rounded-lg shadow-sm bg-white w-full p-3 md:p-4 overflow-x-auto">
                              <div
                                className="w-full py-1 answer"
                                dangerouslySetInnerHTML={{
                                  __html: choice.answerHTML,
                                }}
                              />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      <SolveBottomBar
        currentIndex={currentIndex}
        totalCount={totalCount}
        remainingSubmissions={remainingSubmissions}
        isSubmitting={isSubmitting}
        isTempSubmitting={isTempSubmitting}
        onPrev={handlePrev}
        onNext={handleNext}
        onTempSubmit={handleTempSubmit}
        onSubmit={handleSubmit}
      />

      <FactorTablesModal />
    </>
  );
};

export default SolveAssignmentForm;
